import { Component, Input, ChangeDetectorRef } from '@angular/core';
import * as _ from 'lodash';

import { digitalAssetsSegmentData, AssetFieldItem } from 'shared/digital-assets/digital-assets-segment-data';
import { getFieldByName } from 'utils/utils';
import { isPAG } from 'shared/digital-assets/digital-asset-helpers';

import { BaseScorecardComponent } from '../base/_base-scorecard.component';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';


@Component({
  moduleId: module.id,
  selector: 'digital-assets-scorecard',
  templateUrl: 'digital-assets-scorecard.component.html',
  styleUrls: ['digital-assets-scorecard.component.scss'],
})
export class DigitalAssetsScorecardComponent extends BaseScorecardComponent {
  segmentTitle: string = 'Digital Assets';
  segmentData: any = digitalAssetsSegmentData;
  pathToScorecard: any = 'digitalAssets';

  private pagOnlyFields: Array<string> = ['filePath'];

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailAssets(): Array<any> {
    return _.get(this.itemDetail, 'digitalAssets');
  }

  get missingSize(): number {
    return _.size(_.filter(this.itemDetailAssets, this.isMissingItem));
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
  ) {
    super(ref, scorecardConnectorService);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * $event: {status: string, item: AssetFieldItem, index: number, populated: 1|0}
   */
  protected getItemFieldScoreFromEvent($event: any) {
    return this.getScoreItem($event.item, $event.index);
  }

  /*
    * $event: {item: AssetFieldItem, index: number}
   */
  protected handleControlFocus($event: any) {
    this.handleNestedControlFocus($event);
  }

  /*
    * $event: {index: number}
   */
  protected handleControlRemove($event: any) {
    _.pullAt(this.segmentScores, [$event.index]);
    this.ref.markForCheck();
  }

  /*
    * $event: {assets: Array<any>, controls: Array<FormGroup>}
    * Replace all scores on new because assets may be reordered after assigning new assets
   */
  protected handleControlAdd($event: any) {
    if (!$event?.assets) { return; }
    const segmentScores = _.map($event.assets, (asset: any, assetIndex: number) => {
      return _.reduce(this.scorecardFields, (result: any, field: any) => {
        const itemName = field.path.split('.')[2]; // filename/asset_type/..
        const segmentField = getFieldByName(itemName, this.segmentData);
        const control = _.get($event.controls.at(assetIndex), `controls.${segmentField.field_map}`);
        let valid = _.get(control, 'valid');
        if (itemName === 'filename' && !asset.system_filename && !this.isPAG(asset)) {
          valid = false;
        }
        // pag asset type score should contain segment data related to it
        if (this.isPAG(asset) && !segmentField.is_pag) { return result; }
        // don't add pag asset type fields to the regular asset
        if (!this.isPAG(asset) && _.includes(this.pagOnlyFields, segmentField.field_map)) { return result; }
        // create empty score
        const score = this.getEmptyScoreItem(
          field.validation_rule,
          field.path.replace('%i', assetIndex),
          1,
          (_.get(control, 'value') ? 1 : 0),
          (valid ? 1 : 0),
          1,
          field.required,
        );
        _.set(result, itemName, score);
        return result;
      }, {});
    });
    this.setSegmentScores(segmentScores);
  }

  /*
    * data: {item: AssetFieldItem, index: number}
   */
  protected findScorecardItemViaData(data: any) {
    return this.scorecardItems.toArray()[data.index];
  }

  /*
    * data: {item: AssetFieldItem, index: number}
   */
  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any) {
    this.collapseExpandedChildScores(scorecardItem.scorecardChildItems, data.item.field_map);
  }

  /*
    * data: {item: AssetFieldItem, index: number}
   */
  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    return this.getChildScorecardItemByName(scorecardItem, data.item.field_map);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private isAssetItemValid(index: number): boolean {
    return _.every(_.nth(this.segmentScores, index), (scoreItem: any) => {
      return scoreItem.valid === 1;
    });
  }

  private getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}`);
  }

  private getItemValidationMessage(item: AssetFieldItem, asset: any, index: number) {
    if (item.item_name === 'filename' && !asset.system_filename && !this.isPAG(asset)) {
      return item.errorMessage;
    } else {
      const score = this.getScoreItem(item, index);
      return this.getValidationMessage(item, score);
    }
  }

  private setSegmentScores(scores: Array<any>): void {
    _.set(this.scoreData, `segments.${this.pathToScorecard}.tallies`, scores);
    this.ref.markForCheck();
  }

  private isMissingItem(item: any): boolean {
    return !!_.get(item, 'missing');
  }

  private getMissingPhotoTitle(asset: any): string {
    if (asset.missing_title) {
      return asset.missing_title;
    } else {
        if (this.missingSize > 1) {
          return `Missing Photo ${asset.index} of ${asset.missingSize}`;
        } else {
          return 'Missing Default Photo';
        }
    }
  }

  private isPAG(asset: any): boolean {
    return isPAG(asset);
  }
}
